import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthProvider";
import {
  FaTachometerAlt, FaUsers, FaComments, FaBrain, FaWhatsapp,
  FaEnvelopeOpenText, FaHandshake, FaFlask, FaSignOutAlt, FaBars
} from "react-icons/fa";

// Import your view components
import { OverviewDashboard } from "../components/OverviewDashboard";
import { AgentManagement } from "../components/AgentManagement";
import { ChatHistoryViewer } from "../components/ChatHistoryViewer";
import { AITraining } from "../components/AITraining";
import { WhatsappConnector } from "../components/WhatsappConnector";
import ContactSubmissions from "../components/ContactSubmissions";
import CollaborateSubmissions from "../components/CollaborateSubmissions";
import RequestDemoSubmissions from "../components/RequestDemoSubmissions";


// ===================================================================================
// A. Reusable Navigation Link Component
// This creates the unique "glowing pill" effect for the active link.
// ===================================================================================
const NavLink = ({ item, activeView, setActiveView, setIsSidebarOpen }) => {
  const isActive = activeView === item.view;

  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        setActiveView(item.view);
        setIsSidebarOpen(false);
      }}
      className={`relative flex items-center px-4 py-3 transition-colors duration-200 group ${
        isActive ? "text-white" : "text-gray-400 hover:text-white"
      }`}
    >
      {/* The glowing pill effect - only shown when active */}
      {isActive && (
        <span className="absolute inset-y-0 left-0 w-1 bg-cyan-400 rounded-r-full" aria-hidden="true"></span>
      )}
      <span className="mr-4">{item.icon}</span>
      <span>{item.label}</span>
    </a>
  );
};


// ===================================================================================
// B. The Main Dashboard Shell - Completely Redesigned
// ===================================================================================
export function SuperadminDashboardPage() {
  const { user, logout } = useAuth();
  const [activeView, setActiveView] = useState("overview");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Use a useEffect to close the sidebar on larger screens when resizing
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const viewTitles = {
    overview: "Mission Control",
    agents: "Agent Management",
    history: "Chat History",
    ai_trainer: "AI Knowledge Base",
    whatsapp: "WhatsApp Bot",
    request_demo: "Demo Requests",
    contact_submissions: "Contact Submissions",
    collaborate_submissions: "Collaboration Requests",
  };

  const navItems = [
    { view: "overview", label: "Overview", icon: <FaTachometerAlt /> },
    { view: "agents", label: "Agent Management", icon: <FaUsers /> },
    { view: "history", label: "Chat History", icon: <FaComments /> },
    { view: "ai_trainer", label: "AI Trainer", icon: <FaBrain /> },
    { view: "whatsapp", label: "WhatsApp Bot", icon: <FaWhatsapp /> },
    { view: "contact_submissions", label: "Contact Form", icon: <FaEnvelopeOpenText /> },
    { view: "collaborate_submissions", label: "Collaborate Form", icon: <FaHandshake /> },
    { view: "request_demo", label: "Demo Requests", icon: <FaFlask /> },
  ];

  const renderActiveView = () => {
    // This will render your existing, unstyled components for now.
    // We will style them one by one to match the new design.
    switch (activeView) {
      case "overview": return <OverviewDashboard />;
      case "agents": return <AgentManagement />;
      case "history": return <ChatHistoryViewer />;
      case "ai_trainer": return <AITraining />;
      case "whatsapp": return <WhatsappConnector />;
      case "contact_submissions": return <ContactSubmissions />;
      case "collaborate_submissions": return <CollaborateSubmissions />;
      case "request_demo": return <RequestDemoSubmissions />;
      default: return <OverviewDashboard />;
    }
  };
  
  // The content for the sidebar, used in both mobile and desktop views
  const SidebarContent = () => (
    <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center h-24 px-6">
          
            <span className="text-2xl font-bold text-white tracking-wide">VOID</span>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 space-y-2 px-2">
            {navItems.map((item) => (
                <NavLink 
                    key={item.view} 
                    item={item} 
                    activeView={activeView}
                    setActiveView={setActiveView} 
                    setIsSidebarOpen={setIsSidebarOpen}
                />
            ))}
        </nav>
        
        {/* Logout Button */}
        <div className="px-4 py-6">
            <button
              onClick={logout}
              className="w-full flex items-center justify-center p-3 rounded-lg text-gray-300 bg-gray-800 hover:bg-red-500/80 hover:text-white transition-colors duration-200"
            >
              <FaSignOutAlt className="mr-3" />
              Log Out
            </button>
        </div>
    </div>
  );

  return (
    <div className="h-screen w-full bg-gray-900 text-gray-300 font-sans flex">
      {/* Static Sidebar for Desktop */}
      <aside className="w-64 flex-shrink-0 bg-gray-900 border-r border-gray-800 hidden lg:block">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-y-auto">
        {/* Header */}
        <header className="flex items-center h-24 px-6 md:px-10 flex-shrink-0">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 mr-4 text-gray-400 rounded-md hover:bg-gray-800 lg:hidden"
            aria-label="Open sidebar"
          >
            <FaBars size={24} />
          </button>
          <h1 className="text-3xl font-bold text-white">
            {viewTitles[activeView]}
          </h1>
        </header>

        {/* Page Content */}
        <div className="flex-1 px-6 md:px-10 pb-10">
          {renderActiveView()}
        </div>
      </main>
      
      {/* Mobile Sidebar (Off-canvas) */}
      {isSidebarOpen && (
        <div className="lg:hidden">
          <div 
            className="fixed inset-0 bg-black/50 z-40" 
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="fixed inset-y-0 left-0 w-64 bg-gray-900 z-50 border-r border-gray-800">
            <SidebarContent />
          </div>
        </div>
      )}
    </div>
  );
}